/**
 * Codes d'erreur stables exposés par l'API via `extensions.code` (GraphQL) ou
 * le body `{ code }` (REST). Miroir de apps/api/src/common/errors/error-codes.ts
 * — le contrat API fait foi, garder les deux fichiers synchronisés.
 */
export const ErrorCode = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  INTERNAL_ERROR: 'INTERNAL_ERROR',

  INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  SIRET_ALREADY_USED: 'SIRET_ALREADY_USED',
  EMAIL_VERIFICATION_FAILED: 'EMAIL_VERIFICATION_FAILED',

  // Invitations (CAHIER-ORGANISATION §9). INVITATION_INVALID couvre inconnu,
  // expiré et déjà utilisé — l'écran public ne détaille jamais (IV-08).
  INVITATION_INVALID: 'INVITATION_INVALID',
  EMAIL_ALREADY_USED: 'EMAIL_ALREADY_USED',

  // Gestion d'équipe (FR-02) : invitation, rôles, désactivation.
  USER_ALREADY_MEMBER: 'USER_ALREADY_MEMBER',
  INVALID_INVITATION_ROLE: 'INVALID_INVITATION_ROLE',
  PLAN_SEATS_EXCEEDED: 'PLAN_SEATS_EXCEEDED',
  INVITATION_NOT_FOUND: 'INVITATION_NOT_FOUND',
  CANNOT_CHANGE_OWN_ROLE: 'CANNOT_CHANGE_OWN_ROLE',
  CANNOT_DEACTIVATE_SELF: 'CANNOT_DEACTIVATE_SELF',
  LAST_ACTIVE_OWNER: 'LAST_ACTIVE_OWNER',

  // Tenant / admin users (CAHIER-FONDATION §3, §6).
  CROSS_TENANT_ACCESS: 'CROSS_TENANT_ACCESS',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  CANNOT_SUSPEND_SELF: 'CANNOT_SUSPEND_SELF',

  // Billing (CAHIER-SUBSCRIPTION §7). SUBSCRIPTION_INACTIVE couvre tous les
  // états non payants — le front route vers le paywall (Abonnement) sans
  // détailler pourquoi.
  SUBSCRIPTION_INACTIVE: 'SUBSCRIPTION_INACTIVE',
  SUBSCRIPTION_ALREADY_ACTIVE: 'SUBSCRIPTION_ALREADY_ACTIVE',
  INVALID_PLAN: 'INVALID_PLAN',

  // Clients / chantiers (CAHIER-CHANTIER CH-13) : un chantier ne se rattache
  // qu'à un client vivant — l'écran de création traite ce cas lui-même.
  CLIENT_NOT_FOUND: 'CLIENT_NOT_FOUND',
  CLIENT_ARCHIVED: 'CLIENT_ARCHIVED',
  WORKSITE_NOT_FOUND: 'WORKSITE_NOT_FOUND',
  WORKSITE_LOCKED: 'WORKSITE_LOCKED',
  WORKSITE_COMPLETED: 'WORKSITE_COMPLETED',
  // Envoi du lien de suivi par e-mail (CAHIER-SUIVI-PUBLIC VP-09).
  SHARE_NOT_ACTIVE: 'SHARE_NOT_ACTIVE',
  CLIENT_EMAIL_MISSING: 'CLIENT_EMAIL_MISSING',

  // Déroulé (CAHIER-ETAPE-AFFECTATION §11).
  STAGE_NOT_FOUND: 'STAGE_NOT_FOUND',
  STAGE_LIMIT_REACHED: 'STAGE_LIMIT_REACHED',
  SUBCONTRACTOR_NOT_FOUND: 'SUBCONTRACTOR_NOT_FOUND',
  SUBCONTRACTOR_ARCHIVED: 'SUBCONTRACTOR_ARCHIVED',
  INVALID_STAGE_ORDER: 'INVALID_STAGE_ORDER',
  PROGRESS_DERIVED: 'PROGRESS_DERIVED',
  ASSIGNMENT_NOT_FOUND: 'ASSIGNMENT_NOT_FOUND',
  ASSIGNMENT_ALREADY_EXISTS: 'ASSIGNMENT_ALREADY_EXISTS',

  // MFA (front à venir — miroir du contrat API).
  MFA_ALREADY_ENABLED: 'MFA_ALREADY_ENABLED',
  MFA_SETUP_IN_PROGRESS: 'MFA_SETUP_IN_PROGRESS',
  MFA_NOT_INITIALIZED: 'MFA_NOT_INITIALIZED',
  MFA_INVALID_CODE: 'MFA_INVALID_CODE',
  MFA_VERIFICATION_FAILED: 'MFA_VERIFICATION_FAILED',

  // Journal
  JOURNAL_ENTRY_NOT_FOUND: 'JOURNAL_ENTRY_NOT_FOUND',

  // Media
  INVALID_FILE: 'INVALID_FILE',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  MEDIA_FORMAT_UNSUPPORTED: 'MEDIA_FORMAT_UNSUPPORTED',
  MEDIA_NOT_FOUND: 'MEDIA_NOT_FOUND',
  VOICE_NOTE_JOB_NOT_FOUND: 'VOICE_NOTE_JOB_NOT_FOUND',
  VOICE_NOTE_AUDIO_EXPIRED: 'VOICE_NOTE_AUDIO_EXPIRED',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];
